import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../services/api"
import { FieldRow, FormPage } from "../../../../components/FormPage"
import { useApplicationKeyValuePairTextFieldRenderers } from "../../../../components/Helpers"
import { ApplicationKeyValuePairPost } from "../../../../models/ApplicationKeyValuePair"

const validationSchema = z.object({
  key: z.string().min(1, "Key is required"),
  value: z.string().min(1, "Value is required"),
})

export function ApplicationKeyValuePairCreate() {
  const { applicationId } = useParams()
  const navigate = useNavigate()
  const returnURL = `/applications/${applicationId}#key-value-pairs`

  const {
    handleSubmit,
    reset,
    control,
    formState: { isSubmitting, isDirty, isValid, errors },
  } = useForm<ApplicationKeyValuePairPost>({
    resolver: zodResolver(validationSchema),
  })

  useEffect(() => {
    reset({
      key: "",
      value: "",
    })
  }, [reset])

  const onSubmit = (values: ApplicationKeyValuePairPost) => digitalManagerApi
      .post(`/applications/${applicationId}/keyValuePairs`, values)
      .then((res) => {
        if (res.status === StatusCodes.CREATED) {
          toast.success("Application key-value pair created")
          navigate(returnURL)
        }
      })
      .catch(() => {
        toast.error("Cannot create application key-value pair")
      })

  const handleKeyChange = useCallback(
    (field: ControllerRenderProps<ApplicationKeyValuePairPost, "key">) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const handleValueChange = useCallback(
    (field: ControllerRenderProps<ApplicationKeyValuePairPost, "value">) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const { renderTextFieldKey: renderNameTextFieldKey, renderTextFieldValue } =
    useApplicationKeyValuePairTextFieldRenderers<ApplicationKeyValuePairPost>({
      errors,
      handleKeyChange,
      handleValueChange,
    })

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
    >
      <FieldRow label="Key">
        <Controller name="key" control={control} render={renderNameTextFieldKey} />
      </FieldRow>
      <FieldRow label="Value">
        <Controller name="value" control={control} render={renderTextFieldValue} />
      </FieldRow>
    </FormPage>
  )
}
